import { SquareCheck } from "lucide-react";

import type { PMIssue } from "@/domains/pm/types";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

/**
 * "3/5" checklist progress for a list row or a board card. Renders nothing
 * for an issue without a checklist, or before the counts are hydrated.
 */
export function ChecklistProgressBadge({
  issue,
  className,
}: {
  issue: Pick<PMIssue, "checklist_total" | "checklist_done">;
  className?: string;
}) {
  const { t } = useLanguage();
  const total = issue.checklist_total ?? 0;
  if (total === 0) return null;
  const done = issue.checklist_done ?? 0;
  const complete = done >= total;
  const values = { done: String(done), total: String(total) };
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 text-[11px] tabular-nums",
        complete ? "font-medium text-green-700 dark:text-green-500" : "text-muted-foreground",
        className
      )}
      title={fill(t.pm.issues.checklist.progressLabel, values)}
      aria-label={fill(t.pm.issues.checklist.progressLabel, values)}
    >
      <SquareCheck className="h-3.5 w-3.5" aria-hidden />
      {fill(t.pm.issues.checklist.progress, values)}
    </span>
  );
}
